/**
 * Подсказка «листай свайпом» — при первом входе (15.10.2026).
 *
 * Экран затемняется, рука показывает жест, рядом — что так можно. Тап
 * убирает подсказку и больше ничего не нажимает: под ней может оказаться
 * «Сканировать», и человек, целивший в «Понятно», открыл бы камеру. Свайп
 * срабатывает сразу — его подсказка и учит. Второй раз её не показывают.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icon.js';
import { dismissHint } from './store.js';

/** Ключи «уже видел» в `dismissedHints`. */
export const SWIPE_SECTIONS_HINT = 'swipe-sections';
/** С 27.10.2026 — новая форма записи: счёт карточкой, которую тоже листают; подсказка показывается ещё раз. */
export const SWIPE_OPTABS_HINT = 'swipe-optabs-2';
/**
 * Свайп по строке операции: «Изменить» и «Удалить». Показывается на примере
 * строки — ключ новый: первая подсказка (рука со стрелками) жест не объясняла.
 */
export const SWIPE_ROWS_HINT = 'swipe-rows-example';

/** Сколько подсказка видна, прежде чем касание её уберёт: иначе её сметёт тот же тап, что её открыл. */
const ARM_MS = 350;
/** Сколько она тает. */
const LEAVE_MS = 320;

/**
 * Увиденные за этот запуск. Хранилище запоминает навсегда, но состояние
 * экрана перечитывается не сразу — без этого подсказка показалась бы ещё раз,
 * если закрыть лист операции, ничего не записав.
 */
const seen = new Set<string>();
/**
 * Показывать ли подсказку: её ещё не видели ни раньше, ни сейчас, и экран
 * сенсорный. С мышью пальцем не листают (22.10.2026, компьютер) — там она
 * не показывается и не считается увиденной: на телефоне её покажут.
 */
export const coachWanted = (dismissed: readonly string[], key: string): boolean =>
  !dismissed.includes(key) && !seen.has(key) && !automated() && touchScreen();

const touchScreen = (): boolean => typeof matchMedia !== 'function' || matchMedia('(pointer: coarse)').matches;

/**
 * Сквозные проверки (`tools/`) жмут кнопки сразу после входа — подсказка
 * перехватывала бы первое нажатие каждой. Её саму проверяют с флагом
 * `skoka:coach` в localStorage.
 */
function automated(): boolean {
  if (typeof navigator === 'undefined' || !navigator.webdriver) return false;
  try { return localStorage.getItem('skoka:coach') !== '1'; } catch { return true; }
}
/** Подсказку увидели — больше не показывать. */
export async function coachSeen(key: string): Promise<void> {
  seen.add(key);
  await dismissHint(key);
}

export function SwipeCoach({ title, text, onDone, kind, demo }: {
  title: string; text: string; onDone: () => void;
  /**
   * Пример вместо руки со стрелками: жест показан на том, к чему он
   * относится, — например, на строке операции, даже если своих ещё нет.
   */
  demo?: ReactNode;
  /**
   * Чей свайп учит подсказка: свайп по ней листает именно это. У строки
   * операции жест один — влево, и рука показывает только его.
   */
  kind: 'sections' | 'optabs' | 'rows';
}) {
  const [phase, setPhase] = useState<'on' | 'leaving' | 'off'>('on');
  const done = useRef(onDone);
  done.current = onDone;
  /*
   * Подсказка не исчезает в момент касания, а тает: тап после касания
   * приходит отдельным событием, и, пропади она сразу, он нажал бы то, что
   * под ней. Пока тает, нажатие принимает она сама.
   */
  const close = useRef(() => {});
  close.current = () => {
    if (phase !== 'on') return;
    setPhase('leaving');
    window.setTimeout(() => { setPhase('off'); done.current(); }, LEAVE_MS);
  };
  useEffect(() => {
    const end = () => close.current();
    const t = window.setTimeout(() => {
      document.addEventListener('touchend', end, { capture: true, passive: true });
      document.addEventListener('keydown', end, { capture: true });
    }, ARM_MS);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('touchend', end, { capture: true });
      document.removeEventListener('keydown', end, { capture: true });
    };
  }, []);
  if (phase === 'off' || typeof document === 'undefined') return null;
  return createPortal(
    <div className={`coach${phase === 'leaving' ? ' out' : ''}`} role="dialog" aria-label={title} data-for={kind}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); close.current(); }}>
      <div className="coach-box">
        {demo ?? (
          <div className="coach-hand" aria-hidden="true">
            <Icon name="chevron-left" size={18} className="coach-arr l" />
            <span className="coach-finger"><Icon name="hand-pointer" size={46} /></span>
            <Icon name="chevron-right" size={18} className="coach-arr r" />
          </div>
        )}
        <b className="coach-title">{title}</b>
        <p className="coach-text">{text}</p>
        <button className="coach-ok">Понятно</button>
      </div>
    </div>,
    document.body,
  );
}
